import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const Footer = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const footerContent = {
    en: {
      tagline: "Bridging authentic voices with meaningful brands",
      newsletter: {
        title: "Stay Updated",
        subtitle: "Get the latest creator economy insights and platform updates",
        placeholder: "Enter your email",
        button: "Subscribe",
        success: "Thank you for subscribing!"
      },
      links: {
        platform: {
          title: "Platform",
          items: [
            { name: "For Brands", path: "/brand-campaign-studio" },
            { name: "For Creators", path: "/creator-dashboard" },
            { name: "Content Tools", path: "/content-creation-suite" },
            { name: "Analytics", path: "/analytics-command-center" }
          ]
        },
        community: {
          title: "Community",
          items: [
            { name: "Success Stories", path: "/community-showcase" },
            { name: "Trust Hub", path: "/trust-and-verification-hub" },
            { name: "Creator Resources", path: "/creator-resources" },
            { name: "Brand Guidelines", path: "/brand-guidelines" }
          ]
        },
        support: {
          title: "Support",
          items: [
            { name: "Help Center", path: "/help" },
            { name: "Contact Us", path: "/contact" },
            { name: "API Documentation", path: "/api-docs" },
            { name: "Status Page", path: "/status" }
          ]
        },
        legal: {
          title: "Legal",
          items: [
            { name: "Privacy Policy", path: "/privacy" },
            { name: "Terms of Service", path: "/terms" },
            { name: "Cookie Policy", path: "/cookies" },
            { name: "GDPR Compliance", path: "/gdpr" }
          ]
        }
      },
      social: {
        title: "Follow Us",
        platforms: [
          { name: "Twitter", icon: "Twitter", url: "https://twitter.com/" },
          { name: "LinkedIn", icon: "Linkedin", url: "https://linkedin.com/company/" },
          { name: "Instagram", icon: "Instagram", url: "https://instagram.com/" },
          { name: "YouTube", icon: "Youtube", url: "https://youtube.com/" }
        ]
      },
      contact: {
        title: "Get in Touch",
        email: "hello@creatorbridge.com",
        phone: "+971 4 123 4567",
        address: "Dubai Internet City, UAE"
      },
      copyright: `© ${new Date()?.getFullYear()} CreatorBridge. All rights reserved.`,
      madeWith: "Made with ❤️ by Devora"
    },
    ar: {
      tagline: "ربط الأصوات الأصيلة بالعلامات التجارية المؤثرة",
      newsletter: {
        title: "ابق على اطلاع",
        subtitle: "احصل على أحدث رؤى اقتصاد المبدعين وتحديثات المنصة",
        placeholder: "أدخل بريدك الإلكتروني",
        button: "اشترك",
        success: "شكراً لك على الاشتراك!"
      },
      links: {
        platform: {
          title: "المنصة",
          items: [
            { name: "للعلامات التجارية", path: "/brand-campaign-studio" },
            { name: "للمبدعين", path: "/creator-dashboard" },
            { name: "أدوات المحتوى", path: "/content-creation-suite" },
            { name: "التحليلات", path: "/analytics-command-center" }
          ]
        },
        community: {
          title: "المجتمع",
          items: [
            { name: "قصص النجاح", path: "/community-showcase" },
            { name: "مركز الثقة", path: "/trust-and-verification-hub" },
            { name: "موارد المبدعين", path: "/creator-resources" },
            { name: "إرشادات العلامة التجارية", path: "/brand-guidelines" }
          ]
        },
        support: {
          title: "الدعم",
          items: [
            { name: "مركز المساعدة", path: "/help" },
            { name: "اتصل بنا", path: "/contact" },
            { name: "وثائق API", path: "/api-docs" },
            { name: "صفحة الحالة", path: "/status" }
          ]
        },
        legal: {
          title: "قانوني",
          items: [
            { name: "سياسة الخصوصية", path: "/privacy" },
            { name: "شروط الخدمة", path: "/terms" },
            { name: "سياسة ملفات تعريف الارتباط", path: "/cookies" },
            { name: "امتثال GDPR", path: "/gdpr" }
          ]
        }
      },
      social: {
        title: "تابعنا",
        platforms: [
          { name: "تويتر", icon: "Twitter", url: "https://twitter.com/creatorbridge" },
          { name: "لينكد إن", icon: "Linkedin", url: "https://linkedin.com/company/creatorbridge" },
          { name: "إنستغرام", icon: "Instagram", url: "https://instagram.com/creatorbridge" },
          { name: "يوتيوب", icon: "Youtube", url: "https://youtube.com/creatorbridge" }
        ]
      },
      contact: {
        title: "تواصل معنا",
        email: "hello@creatorbridge.com",
        phone: "+971 4 123 4567",
        address: "مدينة دبي للإنترنت، الإمارات"
      },
      copyright: `© ${new Date()?.getFullYear()} CreatorBridge. جميع الحقوق محفوظة.`,
      madeWith: "صُنع بـ ❤️  من قبل ديف اورا"
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const handleNewsletterSubmit = (e) => {
    e?.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  const content = footerContent?.[currentLanguage];

  return (
    <footer className={`bg-foreground text-background ${currentLanguage === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className={`lg:col-span-1 space-y-6 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
            <Link to="/homepage" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-cultural rounded-lg flex items-center justify-center">
                <Icon name="Bridge" size={24} color="white" />
              </div>
              <span className="text-2xl font-bold text-secondary">CreatorBridge</span>
            </Link>
            
            <p className="text-background/80 leading-relaxed">
              {content?.tagline}
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <h4 className="font-semibold text-background">{content?.contact?.title}</h4>
              <div className="space-y-2 text-sm">
                <div className={`flex items-center space-x-2 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Icon name="Mail" size={16} color="var(--color-secondary)" />
                  <a href={`mailto:${content?.contact?.email}`} className="text-background/80 hover:text-secondary transition-colors">
                    {content?.contact?.email}
                  </a>
                </div>
                <div className={`flex items-center space-x-2 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Icon name="Phone" size={16} color="var(--color-secondary)" />
                  <a href={`tel:${content?.contact?.phone}`} className="text-background/80 hover:text-secondary transition-colors">
                    {content?.contact?.phone}
                  </a>
                </div>
                <div className={`flex items-center space-x-2 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <Icon name="MapPin" size={16} color="var(--color-secondary)" />
                  <span className="text-background/80">{content?.contact?.address}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Links Sections */}
          <div className="lg:col-span-2 grid md:grid-cols-3 gap-8">
            {Object.entries(content?.links)?.map(([key, section]) => (
              <div key={key} className={`space-y-4 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
                <h4 className="font-semibold text-background">{section?.title}</h4>
                <ul className="space-y-2">
                  {section?.items?.map((item, index) => (
                    <li key={index}>
                      <Link
                        to={item?.path}
                        className="text-background/80 hover:text-secondary transition-colors text-sm"
                      >
                        {item?.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter Section */}
          <div className={`space-y-6 ${currentLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
            <div>
              <h4 className="font-semibold text-background mb-2">{content?.newsletter?.title}</h4>
              <p className="text-background/80 text-sm">{content?.newsletter?.subtitle}</p>
            </div>

            {!subscribed ? (
              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder={content?.newsletter?.placeholder}
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="bg-background/10 border-background/20 text-background placeholder:text-background/60"
                  required
                />
                <Button 
                  type="submit" 
                  variant="default" 
                  size="sm" 
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
                >
                  {content?.newsletter?.button}
                </Button>
              </form>
            ) : (
              <div className="bg-success/20 border border-success/30 rounded-lg p-4">
                <p className="text-success text-sm">{content?.newsletter?.success}</p>
              </div>
            )}

            {/* Social Links */}
            <div className="space-y-4">
              <h4 className="font-semibold text-background">{content?.social?.title}</h4>
              <div className={`flex space-x-4 ${currentLanguage === 'ar' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                {content?.social?.platforms?.map((platform) => (
                  <a
                    key={platform?.name}
                    href={platform?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-background/10 rounded-lg flex items-center justify-center hover:bg-secondary/20 transition-colors"
                  >
                    <Icon name={platform?.icon} size={20} color="var(--color-secondary)" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-background/20">
        <div className="container mx-auto px-4 py-6">
          <div className={`flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 ${currentLanguage === 'ar' ? 'md:flex-row-reverse' : ''}`}>
            <div className="text-background/80 text-sm">
              {content?.copyright}
            </div>
            <div className="text-background/60 text-sm">
              {content?.madeWith}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;